import { useEffect, useState } from 'react';

import { levelDb, meterRatio, rmsLevel } from './audioLevel';

/** Minimum level delta that updates the meter; keeps the render rate sane without visible stutter. */
const LEVEL_EPSILON = 0.01;

/** Live microphone reading: the 0..1 meter ratio and the raw RMS in dBFS (clamped to [-48, 0]). */
export interface MicLevelReading {
  readonly level: number;
  readonly db: number;
}

const SILENT_READING: MicLevelReading = { level: 0, db: -48 };

/**
 * Live microphone reading for a local preview stream, computed with WebAudio (AnalyserNode over
 * the stream): `level` is the perceptual 0..1 meter ratio, `db` the raw RMS in dBFS for the
 * numeric readout. Without an AudioContext — jsdom, insecure contexts — the reading stays
 * silent. The pure math lives in audioLevel.ts and is unit-tested there.
 */
export function useMicLevel(stream: MediaStream | null): MicLevelReading {
  const [reading, setReading] = useState<MicLevelReading>(SILENT_READING);

  useEffect(() => {
    if (stream === null) {
      setReading(SILENT_READING);
      return;
    }
    const audioContextCtor: typeof AudioContext | undefined = globalThis.AudioContext;
    if (audioContextCtor === undefined) {
      return;
    }

    let context: AudioContext | null = null;
    try {
      context = new audioContextCtor();
    } catch {
      return;
    }
    let source: MediaStreamAudioSourceNode;
    let analyser: AnalyserNode;
    try {
      source = context.createMediaStreamSource(stream);
      analyser = context.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
    } catch {
      void context.close();
      return;
    }
    const samples = new Float32Array(analyser.fftSize);

    let frame = 0;
    let last = 0;
    const tick = (): void => {
      analyser.getFloatTimeDomainData(samples);
      const rms = rmsLevel(samples);
      const next = meterRatio(rms);
      if (Math.abs(next - last) >= LEVEL_EPSILON || (next === 0 && last !== 0)) {
        last = next;
        setReading({ level: next, db: levelDb(rms) });
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      source.disconnect();
      analyser.disconnect();
      void context?.close();
      setReading(SILENT_READING);
    };
  }, [stream]);

  return reading;
}
